import { Suspense } from "react";
import { ClientLoadable, useClientLoadable } from "./client-loadable";
import { ServerLoadable } from "./server-loadable";

export type RootParams = {
    path?: { [key: string]: any };
    query?: { [key: string]: string };
    headers?: Record<string, string>;
};

type ResponseOptions<T> = {
    type?: 'json' | 'text' | 'raw';
    nonOk?: T;
    logFailure?: boolean;
    stopFailure?: boolean;
};

export type TRequestable<TResponse = unknown, TParams extends RootParams = {}> = {
    fetch: (params?: TParams) => Promise<TResponse>;
    Loadable: ({ render, params, dependencies, skeleton, empty, alwaysShowSkeleton, emptyOnEmptyArray }: {
        render: (value: TResponse) => React.ReactNode | void;
        params?: TParams;
        dependencies?: any[];
        skeleton?: React.ReactNode;
        empty?: React.ReactNode;
        alwaysShowSkeleton?: boolean;
        emptyOnEmptyArray?: boolean;
        onIntercept?: (response: TResponse) => void;
    }) => React.ReactNode;
    ServerLoadable: ({ render, params, skeleton, empty, alwaysShowSkeleton, emptyOnEmptyArray }: {
        render: (value: TResponse) => React.ReactNode | void;
        params?: TParams;
        skeleton?: React.ReactNode;
        empty?: React.ReactNode;
        alwaysShowSkeleton?: boolean;
        emptyOnEmptyArray?: boolean;
    }) => React.ReactNode;
    useLoadable: (params?: TParams, dependencies?: any[]) => [response: TResponse, loading: boolean];
};

export type TRequestableParams<TResponse = unknown, TParams = undefined> = {
    fetch: (params: TParams) => Promise<TResponse>;
    Loadable: ({ render, params, dependencies, skeleton, empty, alwaysShowSkeleton, emptyOnEmptyArray }: {
        params: TParams;
        render: (value: TResponse) => React.ReactNode | void;
        dependencies?: any[];
        skeleton?: React.ReactNode;
        empty?: React.ReactNode;
        alwaysShowSkeleton?: boolean;
        emptyOnEmptyArray?: boolean;
        onIntercept?: (response: TResponse) => void;
    }) => React.ReactNode;
    ServerLoadable: ({ render, params, skeleton, empty, alwaysShowSkeleton, emptyOnEmptyArray }: {
        params: TParams;
        render: (value: TResponse) => React.ReactNode | void;
        skeleton?: React.ReactNode;
        empty?: React.ReactNode;
        alwaysShowSkeleton?: boolean;
        emptyOnEmptyArray?: boolean;
    }) => React.ReactNode;
    useLoadable: (parameters: TParams, dependencies?: any[]) => [response: TResponse, loading: boolean];
};

export abstract class Requestable<TResponse = unknown, TParams extends RootParams = {}> {
    #host?: string = 'https://core-api-fklqeyhovq-uc.a.run.app';
    #path: string;
    #method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    #responseOptions: ResponseOptions<TResponse>;

    constructor(method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH', path: string, responseOptions: ResponseOptions<TResponse> = {}) {
        this.#path = path;
        this.#method = method;
        this.#responseOptions = responseOptions;
        this.Loadable = this.Loadable.bind(this);
        this.fetch = this.fetch.bind(this);
        this.useLoadable = this.useLoadable.bind(this);
    }

    abstract fillRequest(request: RequestInit, params?: TParams): void;

    async fetch(params?: TParams): Promise<TResponse> {
        if (this.#host == undefined) throw new Error('Cannot call this API as the host does not exist');

        const request: RequestInit = { method: this.#method, headers: {} };
        this.fillRequest(request, params);

        if (typeof window === "undefined") {
            const { cookies } = await import("next/headers");
            if (cookies != undefined) {
                let cookeez = [...(await cookies()).getAll()];
                let imitate = cookeez.findIndex(c => c.name.startsWith('imitate-session_'));
                if (imitate != -1) {
                    const newName = cookeez[imitate].name.substring('imitate-session_'.length);
                    cookeez = cookeez.filter(c => c.name !== newName);
                    imitate = cookeez.findIndex(c => c.name.startsWith('imitate-session_'));
                    cookeez[imitate] = { ...cookeez[imitate] };
                    cookeez[imitate].name = newName;
                }
                //@ts-ignore
                request.headers['Cookie'] = (request.headers['Cookie'] && request.headers['Cookie'].length > 0 ? request.headers['Cookie'] + "; " : '') + cookeez.map(c => c.name + '=' + c.value).reduce((c1, c2) => c1 + '; ' + c2, '');
            }
        } else {
            request.credentials = 'include';
        }

        if (params?.headers) {
            for (const [k, v] of Object.entries(params.headers)) {
                //@ts-ignore
                request.headers[k] = v;
            }
        }

        let path = this.#path.replace(/:(\w+)/g, (_, key) => {
            if (params?.path && key in params.path) return encodeURIComponent(params.path[key]);
            throw new Error(`Missing parameter: ${key}`);
        });

        if (path.startsWith('/') && this.#host.endsWith('/')) path = path.substring(1);
        else if (!path.startsWith('/') && !this.#host.endsWith('/')) path = '/' + path;

        if (params?.query) path += '?' + new URLSearchParams(params.query).toString();

        const { type = 'json', nonOk = { error: true } as TResponse, logFailure = true, stopFailure = false } = this.#responseOptions;

        return await fetch(this.#host + path, request).then(r => {
            if (!r.ok) {
                if (logFailure) r.clone().text().then(e => console.error(`An error occured while fetching (${path}), response(${r.status}):`, e));
                if (stopFailure) return nonOk;
            }

            switch (type) {
                case 'raw': return r as TResponse;
                case 'text': return r.text().catch(() => undefined) as TResponse;
                default: return r.json().catch(() => undefined);
            }
        }, err => {
            if (logFailure) console.error(`An error occured while executing (${path}), error:`, err);
            return nonOk;
        });
    }

    Loadable({ render, params, dependencies, skeleton, empty, emptyOnEmptyArray, onIntercept }: {
        params: TParams;
        render: (value: TResponse) => React.ReactNode | null;
        dependencies?: any[];
        skeleton?: React.ReactNode;
        empty?: React.ReactNode;
        emptyOnEmptyArray?: boolean;
        onIntercept?: (response: TResponse) => void;
    }) {
        'use client';
        return <Suspense fallback={skeleton}><ClientLoadable<TParams, TResponse> params={params} fetch={this.fetch} render={render} empty={empty} emptyOnEmptyArray={emptyOnEmptyArray} dependencies={dependencies} onIntercept={onIntercept} /></Suspense>;
    }

    ServerLoadable({ render, params, skeleton, empty, emptyOnEmptyArray }: {
        params: TParams;
        render: (value: TResponse) => React.ReactNode | null;
        skeleton?: React.ReactNode;
        empty?: React.ReactNode;
        emptyOnEmptyArray?: boolean;
    }) {
        return <Suspense fallback={skeleton}><ServerLoadable<TParams, TResponse> params={params} fetch={this.fetch} render={render} empty={empty} emptyOnEmptyArray={emptyOnEmptyArray} /></Suspense>;
    }

    useLoadable(params: TParams, dependencies?: any[]) {
        return useClientLoadable(this.fetch, params, dependencies);
    }

    setHost(host: string) {
        this.#host = host;
    }

    getHost() {
        return this.#host;
    }
}

class GetRequestable<TResponse = unknown, TParams extends RootParams = {}> extends Requestable<TResponse, TParams> {
    constructor(path: string, responseOptions?: ResponseOptions<TResponse>) {
        super('GET', path, responseOptions);
    }

    fillRequest(): void { }
}

type BodyParams = { body?: any; };
type BodyRequest = { bodyType?: 'json' | 'string' | 'file' | 'blob' | 'form'; };

class PostRequestable<TResponse = unknown, TParams extends RootParams & BodyParams = {}> extends Requestable<TResponse, TParams> {
    #requestParams: BodyRequest;

    constructor(path: string, requestParams: BodyRequest = {}, responseOptions?: ResponseOptions<TResponse>) {
        super('POST', path, responseOptions);
        this.#requestParams = requestParams;
    }

    fillRequest(request: RequestInit, params?: TParams): void {
        fillBody(request, params?.body, this.#requestParams.bodyType);
    }
}

class PutRequestable<TResponse = unknown, TParams extends RootParams & BodyParams = {}> extends Requestable<TResponse, TParams> {
    #requestParams: BodyRequest;

    constructor(path: string, requestParams: BodyRequest = {}, responseOptions?: ResponseOptions<TResponse>) {
        super('PUT', path, responseOptions);
        this.#requestParams = requestParams;
    }

    fillRequest(request: RequestInit, params?: TParams): void {
        fillBody(request, params?.body, this.#requestParams.bodyType);
    }
}

class DeleteRequestable<TResponse = unknown, TParams extends RootParams = {}> extends Requestable<TResponse, TParams> {
    constructor(path: string, responseOptions?: ResponseOptions<TResponse>) {
        super('DELETE', path, responseOptions);
    }

    fillRequest(): void { }
}

function fillBody(request: RequestInit, body: any, type: BodyRequest['bodyType'] = 'json') {
    if (body == undefined) return;

    switch (type) {
        case 'string':
            //@ts-ignore
            request.headers['Content-Type'] = 'application/txt';
            request.body = body.toString();
            break;
        case 'file':
            //@ts-ignore
            request.headers['Content-Type'] = 'application/txt';
            break;
        case 'blob':
            //@ts-ignore
            request.headers['Content-Type'] = 'application/octet-stream';
            request.body = body;
            break;
        case 'form':
            request.body = body;
            break;
        default:
            //@ts-ignore
            request.headers['Content-Type'] = 'application/json';
            request.body = JSON.stringify(body);
            break;
    }
}

export function get<TResponse = unknown, TParams extends RootParams | undefined = undefined>(path: string, responseOptions?: ResponseOptions<TResponse>): TParams extends undefined ? TRequestable<TResponse, RootParams> : TRequestableParams<TResponse, TParams> {
    return new GetRequestable(path, responseOptions) as any;
}

export function post<TResponse = unknown, TParams extends RootParams & BodyParams | undefined = undefined>(path: string, requestParams?: BodyRequest, responseOptions?: ResponseOptions<TResponse>): TParams extends undefined ? TRequestable<TResponse, RootParams> : TRequestableParams<TResponse, TParams> {
    return new PostRequestable(path, requestParams, responseOptions) as any;
}

export function put<TResponse = unknown, TParams extends RootParams & BodyParams | undefined = undefined>(path: string, requestParams?: BodyRequest, responseOptions?: ResponseOptions<TResponse>): TParams extends undefined ? TRequestable<TResponse, RootParams> : TRequestableParams<TResponse, TParams> {
    return new PutRequestable(path, requestParams, responseOptions) as any;
}

export function del<TResponse = unknown, TParams extends RootParams | undefined = undefined>(path: string, responseOptions?: ResponseOptions<TResponse>): TParams extends undefined ? TRequestable<TResponse, RootParams> : TRequestableParams<TResponse, TParams> {
    return new DeleteRequestable(path, responseOptions) as any;
}